import React, { useEffect, useRef } from 'react';
import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import T from 'prop-types';

import { ContentWrapper } from 'elements';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ReportExportDialog from 'components/ReportExportDialog';
import { getMachine } from 'redux/machines/actions';
import MachineDetailsAccordion from 'containers/Machines/MachinesDetail/MachineDetailsAccordion';
import { DetailsWrapper } from './BatchRecordsDetails.styled';
import InfoBlocks from './InfoBlocks';
import { BatchResponse } from '../../helpers/propTypes';
import BatchNotes from './batchNotes/batchNotes';
import { getBatchNotes, getMachines } from '../../redux/rootSelectors';
import { getAllBatchNotesByBatchIdAction } from '../../redux/batch_notes/actions';
import Breadcrumbs from '../elements/Breadcrumbs';
import Alarms from './Alarms';
import Alerts from './Alerts';
import { isValidDateString } from '../../helpers/utils';
import SensorsChart from './chart/SensorsChart';
import MachineNotFound from '../MachineNotFound/MachineNotFound';
import { isMachineNotFound } from '../../helpers/machineErrors';

const BatchRecordsDetails = ({ data }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const name = data.details.batch_id ? data.details.batch_id : t('batch_records.overview.items');
  const isMobile = useMediaQuery('(max-width:425px)');
  const { getMachineResponse, getMachineStatus } = useSelector(getMachines);
  const chartRef = useRef(null);
  const footerRef = useRef(null);

  function getValidOrNow(date) {
    return isValidDateString(date) ? date : new Date().toISOString();
  }

  const dateFrom = getValidOrNow(data.details.date?.start);
  const dateTo = getValidOrNow(data.details.date?.end);

  // Get all batches notes for batch details
  const { batchNotes, batchLoadingState } = useSelector(getBatchNotes);
  useEffect(() => {
    dispatch(getAllBatchNotesByBatchIdAction(data.details.id));
  }, [dispatch, data]);

  const getAllBatchNotes = () => {
    dispatch(getAllBatchNotesByBatchIdAction(data.details.id));
  };

  useEffect(() => {
    if (data?.machine_id) dispatch(getMachine(data?.machine_id));
  }, [dispatch, data]);

  const breadcrumbObj = [
    { route: '/batch-records', name: `${t('batch_records.title')}` },
    { route: null, name: `${t('batch_records.batch_details')}` }
  ];

  return (
    <>
      <ContentWrapper maxWidth={false}>
        <DetailsWrapper theme={theme}>
          <Breadcrumbs data={breadcrumbObj} />
          <Stack
            direction="row"
            spacing={2}
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>{name}</Typography>
            <ReportExportDialog batch={data} chartRef={chartRef} footerRef={footerRef} />
          </Stack>
          <InfoBlocks data={data} />
          {data.machine_id && isMachineNotFound(getMachineStatus) && <MachineNotFound />}
          {!!getMachineResponse?.id && getMachineResponse.id === data.machine_id && (
            <MachineDetailsAccordion machine={getMachineResponse} />
          )}
          <BatchNotes
            loadingState={batchLoadingState}
            data={Array.isArray(batchNotes) ? batchNotes : []}
            reloadAction={getAllBatchNotes}
            isMobile={isMobile}
          />
          {!!getMachineResponse?.id && getMachineResponse.id === data.machine_id && (
            <SensorsChart
              machineId={getMachineResponse?.id}
              date={data?.details?.date}
              chartRef={chartRef}
              footerRef={footerRef}
            />
          )}
          <Alarms machineId={data.machine?.id} from={dateFrom} to={dateTo} />
          <Alerts machineId={data.machine?.id} from={dateFrom} to={dateTo} />
        </DetailsWrapper>
      </ContentWrapper>
    </>
  );
};

BatchRecordsDetails.propTypes = {
  data: T.shape(BatchResponse).isRequired
};

export default BatchRecordsDetails;
